import React, { useEffect, useState, useCallback, Suspense } from 'react';
import {
  DownloadOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  PlusOutlined,
  RetweetOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import {
  Button,
  Col,
  ConfigProvider,
  Form,
  Modal,
  notification,
  Row,
  Table,
  Tag,
  Tooltip,
  Popover,
  Dropdown,
} from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import {
  debouncedDataLoadWaitMs,
  ERROR_CODES,
  ORDER_TYPES,
  STATUS,
} from 'helpers/constants';
import trTR from 'antd/es/locale/tr_TR';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
import {
  deleteSlotTemplates,
  getSlotTemplates,
  changeStatusSlotTemplates,
} from 'store/requests/slot';
import { resetSlotDays } from '../../../../store/requests/slot';
const Add = React.lazy(() => import('./Add'));
const Edit = React.lazy(() => import('./Edit'));
const SlotsTableFilters = React.lazy(() => import('./SlotsTableFilters'));
let lastQuery = false;

const Slots = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { confirm } = Modal;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [exportButtonLoading, setExportButtonLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRowsFromTable, setSelectedRowsFromTable] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState(true);
  const [changeStatusButtonDisabled, setChangeStatusButtonDisabled] =
    useState(true);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [20, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 20,
    },
  });

  const checkAllActive = (array) => {
    if (array?.length === 0) {
      return false;
    }

    const firstActive = array[0]?.active;

    for (let i = 1; i < array?.length; i++) {
      if (array[i]?.active !== firstActive) {
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    if (selectedRowsFromTable) {
      const isMainTemplate = selectedRowsFromTable.some(
        (row) => row.defaultForAll === true,
      );
      isMainTemplate || selectedRowsFromTable.length === 0
        ? setDeleteButtonDisabled(true)
        : setDeleteButtonDisabled(false);

      const isHaveSameStatus = checkAllActive(selectedRowsFromTable);

      if (
        selectedRowsFromTable.length === 0 ||
        !isHaveSameStatus ||
        isMainTemplate
      ) {
        setChangeStatusButtonDisabled(true);
      } else {
        setChangeStatusButtonDisabled(false);
      }
    }
  }, [selectedRowsFromTable]);

  const fetchCSVData = async () => {
    setExportButtonLoading(true);
    const slotTemplatesRes = await getSlotTemplates('size=2000');
    const templateData = slotTemplatesRes.content;

    if (templateData && templateData.length > 0) {
      const csvData = [
        columns.map((item) => t(item.title)),
        ...templateData.map((item) => [
          item.name,
          item.code,
          item.defaultForAll ? 'Evet' : 'Hayır',
          item.description,
          item.active ? 'Aktif' : 'Pasif',
        ]),
      ];

      downloadCSV(csvData);
      setExportButtonLoading(false);
    }
  };

  const downloadCSV = (csvData) => {
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      csvData.map((row) => row.join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'SlotTemplates.csv');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRowsFromTable(newSelectedRows);
  };
  const rowSelection = {
    selectedRowKeys,
    selectedRowsFromTable,
    onChange: onSelectChange,
  };

  const columns = [
    {
      dataIndex: 'name',
      title: t('slotManagement.templateName'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      dataIndex: 'code',
      title: t('slotManagement.templateCode'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('slotManagement.isMainTemplate'),
      dataIndex: 'defaultForAll',
      sorter: true,
      showSorterTooltip: false,
      render: (defaultForAll) =>
        defaultForAll ? (
          <Tag icon={<CheckCircleOutlined />} color={'green'}>
            {t('yes')}
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color={'lightgrey'}>
            {t('no')}
          </Tag>
        ),
    },
    {
      dataIndex: 'description',
      title: t('slotManagement.description'),
      sorter: true,
      showSorterTooltip: false,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Popover content={text}>
          <div style={{ maxWidth: 150 }}>{text}</div>
        </Popover>
      ),
    },
    {
      title: t('slotManagement.status'),
      dataIndex: 'active',
      sorter: true,
      showSorterTooltip: false,
      render: (active) =>
        active ? (
          <Tag color={'green'}>{t('active')}</Tag>
        ) : (
          <Tag color={'red'}>{t('passive')}</Tag>
        ),
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  label: t('edit'),
                  icon: <EditOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);
                    setSelectedItem(dataItem);
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItem(dataItem)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  function showDeleteTemplate() {
    const ids = selectedRowsFromTable.map((item) => item.id);
    const names = selectedRowsFromTable.map((item) => item.name);
    const nameList = names.join(', ');
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: ` ${nameList} ${t('slotManagement.deleteTemplate')}`,
      okText: t('slotManagement.delete'),
      okType: 'primary',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        deleteSlotTemplates({ ids: ids }).then((res) => {
          if (res === '') {
            notificationDelete(STATUS.SUCCESS);
            lastQuery = null;
            const queryString = getQueryString();
            loadData(queryString);
          } else {
            notification.error({ message: res.message });
          }
        });
      },
      onCancel() {},
    });
  }

  function showChangeStatusOfTemplate() {
    const isActive = selectedRowsFromTable[0]?.active;
    const ids = selectedRowsFromTable.map((item) => item.id);
    const names = selectedRowsFromTable.map((item) => item.name);
    const nameList = names.join(', ');
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: ` ${nameList} ${t('slotManagement.changeStatusTemplate')}`,
      okText: t('slotManagement.changeStatus'),
      okType: 'primary',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        changeStatusSlotTemplates({
          ids: ids,
          active: !isActive,
          forceChange: false,
        })
          .then((res) => {
            notificationChangeStatus(STATUS.SUCCESS);
            lastQuery = null;
            const queryString = getQueryString();
            loadData(queryString);
          })
          .catch((error) => {
            if (error) {
              const errorCode = error.code;
              const errorMessage = error.message;

              if (
                errorCode ===
                ERROR_CODES.SLOT_TEMPLATE_RELATED_WITH_ZONE_EXCEPTION
              ) {
                confirm({
                  title: t('doYouConfirm'),
                  content: errorMessage,
                  onOk: () => {
                    changeStatusSlotTemplates({
                      ids: ids,
                      active: !isActive,
                      forceChange: true,
                    }).then((res) => {
                      notificationChangeStatus(STATUS.SUCCESS);
                      lastQuery = null;
                      const queryString = getQueryString();
                      loadData(queryString);
                    });
                  },
                  onCancel: () => {},
                });
              }
            }
          });
      },
      onCancel() {},
    });
  }

  const notificationChangeStatus = (type) => {
    notification[type]({
      message: t('slotManagement.templateEdited'),
      description: t('slotManagement.templateStatusChanged'),
    });
  };

  const notificationDelete = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('slotManagement.templateDeleted'),
    });
  };

  const getQueryString = () => {
    let defaultUserNameSort = true;
    let queryString = '';

    Object.entries(filters)
      .filter(([item, value]) => value !== null && value !== undefined)
      .forEach(([item, value], idx) => {
        if (idx == 0) {
          queryString += `filter=${item}==${encodeURI(value)}`;
        } else {
          queryString += ` and ${item}==${encodeURI(value)}`;
        }
      });

    queryString = `${queryString}&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;
    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
      defaultUserNameSort = false;
    }
    if (defaultUserNameSort) {
      queryString = `${queryString}&sort=defaultForAll,desc`;
    }
    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (!loading) {
      lastQuery = queryString;
      setLoading(true);
      getSlotTemplates(queryString)
        .then((res) => {
          const { content, page } = res;
          setRows(content ? content : []);
          setSelectedRowsFromTable([]);
          setTimeout(() => {
            setSelectedRowKeys([]);
          }, 1000);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  useEffect(() => {
    fetchData();
    setIsFiltersOpen(false);
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const actions = [
    {
      isCrudEnabled,
      type: 'primary',
      title: t('add'),
      icon: <PlusOutlined />,
      onClick: () => setIsAddModalOpen(true),
      disabled: false,
    },
    {
      isCrudEnabled: true,
      type: '',
      title: t('slotManagement.changeStatus'),
      icon: <RetweetOutlined />,
      onClick: showChangeStatusOfTemplate,
      disabled: changeStatusButtonDisabled,
    },
    {
      isCrudEnabled: true,
      type: '',
      title: t('download'),
      onClick: () => {
        fetchCSVData();
      },
      icon: <DownloadOutlined />,
      disabled: exportButtonLoading,
      loading: exportButtonLoading,
    },
    {
      isCrudEnabled: true,
      type: '',
      title: t('slotManagement.delete'),
      icon: <DeleteOutlined />,
      onClick: showDeleteTemplate,
      disabled: deleteButtonDisabled,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {item.isCrudEnabled &&
            (item.customContent ?? (
              <Button
                type={item?.type}
                shape="round"
                icon={item?.icon}
                onClick={item.onClick}
                disabled={item.disabled}
                style={{ marginBottom: '10px', marginRight: '3px' }}
                danger={item?.danger}
              />
            ))}
        </Tooltip>
      );
    });
  };
  const renderSelectedFilters = () => {
    return Object.keys(filters).map((item, idx) => {
      if (filters[item] !== null && filters[item] !== undefined) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${t(item)} : ${t(`${filters[item]}`)}`}
          </Tag>
        );
      }
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  return (
    <div>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            rowSelection={rowSelection}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <SlotsTableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
          />
        )}
        {isAddModalOpen && (
          <Add
            onCloseHandler={() => {
              setIsAddModalOpen(false);
              resetSlotDays();
            }}
            onSuccessCallback={fetchData}
          />
        )}
        {isEditModalOpen && (
          <Edit
            selectedTemplateId={selectedItem.id}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
      </Suspense>
    </div>
  );
};

export default Slots;
